import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Icon,
  Link,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { RiDownload2Line, RiSearchLine } from 'react-icons/ri';
import * as Yup from 'yup';
import { DatePicker } from '../../../../../../../components/Form/DatePicker';
import { createAppointmentPaymentsReportFileService } from '../../../../../../../services/Appointments/CreateAppointmentPaymentsReportFileService';
import { listAppointmentPaymentReportsService } from '../../../../../../../services/Appointments/ListAppointmentPaymentReportsService';
import {
  maskDateTime,
  maskMoney,
} from '../../../../../../../utils/formatters/handleMask';
import {
  getResponseErrorMessage,
  translateError,
} from '../../../../../../../utils/errors';
import {
  AppointmentPaymentsReportTable,
  IAppointmentPaymentsReportTableItem,
} from './components/AppointmentPaymentsReportTable';

interface ILoadBookingExternalPaymentsReports {
  authorizationDateEnd: Date;
  authorizationDateInit: Date;
  page: number;
}

interface IExportInvoiceReportsFormData {
  authorizationDateEnd: Date;
  authorizationDateInit: Date;
}

const bookingExternalPaymentsReportSchema = Yup.object().shape({
  authorizationDateEnd: Yup.date().nullable().required('Requerido'),
  authorizationDateInit: Yup.date()
    .max(Yup.ref('endDate'), 'Data de início deve ser anterior à data final')
    .nullable()
    .required('Requerido'),
});

export const AppointmentPaymentsReportList = (): JSX.Element => {
  const toast = useToast();

  const [appointmentPaymentsReportList, setAppointmentPaymentsReportList] =
    useState<IAppointmentPaymentsReportTableItem[]>([]);
  const [downloadLinkUrl, setDownloadLinkUrl] = useState<string>();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>();

  const downloadLinkRef = useRef<HTMLAnchorElement>(null);

  const maxDate = useMemo(() => new Date(), []);

  const { register, handleSubmit, formState, setValue, control, watch } =
    useForm({
      resolver: yupResolver(bookingExternalPaymentsReportSchema),
    });

  const { errors } = formState;

  const dateEnd = watch('authorizationDateEnd');
  const dateInit = watch('authorizationDateInit');

  const loadBookingExternalPaymentsReports = useCallback(
    async ({
      authorizationDateEnd,
      authorizationDateInit,
      page,
    }: ILoadBookingExternalPaymentsReports) => {
      try {
        const { items: paymentReports, pages } =
          await listAppointmentPaymentReportsService({
            authorizationDateEnd,
            authorizationDateInit,
            limit: 20,
            order: 'DESC',
            sort: 'createdAt',
            page,
          });

        const parsedPaymentReports =
          paymentReports.map<IAppointmentPaymentsReportTableItem>((report) => ({
            ...report,
            formattedAuthorizedAt: report.authorizedAt
              ? maskDateTime(report.authorizedAt)
              : '-',
            formattedStartAt: report.startAt
              ? maskDateTime(report.startAt)
              : '-',
            formattedFinishAt: report.finishAt
              ? maskDateTime(report.finishAt)
              : '-',
            formattedPrice: maskMoney(report.price * report.quantity),
          }));

        setTotalPages(pages);
        setAppointmentPaymentsReportList(parsedPaymentReports);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              'Ocorreu um erro ao carregar os dados dos pagamentos, tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast],
  );

  const handlePageChange = useCallback(
    async (page: number) => {
      setCurrentPage(page);

      await loadBookingExternalPaymentsReports({
        authorizationDateEnd: dateEnd,
        authorizationDateInit: dateInit,
        page,
      });
    },
    [dateEnd, dateInit, loadBookingExternalPaymentsReports],
  );

  useEffect(() => {
    setValue('endDate', maxDate);
  }, [maxDate, setValue]);

  useEffect(() => {
    if (downloadLinkUrl) {
      downloadLinkRef.current?.click();
    }
  }, [downloadLinkUrl]);

  const handleExportInvoiceReports: SubmitHandler<IExportInvoiceReportsFormData> =
    useCallback(
      async ({ authorizationDateEnd, authorizationDateInit }) => {
        try {
          const fileUrl = await createAppointmentPaymentsReportFileService({
            authorizationDateEnd,
            authorizationDateInit,
            order: 'DESC',
            sort: 'createdAt',
          });

          setDownloadLinkUrl(fileUrl);

          toast({
            title: 'Exportado com sucesso',
            description: 'Relatório foi exportado corretamente',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status !== 401) {
            const errorMessage = await getResponseErrorMessage(
              err.response?.data,
            );

            toast({
              title: 'Falha ao exportar',
              description:
                translateError({ message: errorMessage || '' }) ||
                'Ocorreu um erro ao exportar o relatório, tente novamente',
              status: 'error',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });
          }
        }
      },
      [toast],
    );

  const handleList: SubmitHandler<IExportInvoiceReportsFormData> = useCallback(
    async ({ authorizationDateEnd, authorizationDateInit }) => {
      setCurrentPage(1);

      await loadBookingExternalPaymentsReports({
        authorizationDateEnd,
        authorizationDateInit,
        page: 1,
      });
    },
    [loadBookingExternalPaymentsReports],
  );

  return (
    <Box>
      <Flex
        as="form"
        flexDirection="column"
        onSubmit={handleSubmit(handleList)}
      >
        <ButtonGroup alignSelf="flex-end">
          <Button
            colorScheme="blue"
            size="sm"
            type="submit"
            leftIcon={<Icon as={RiSearchLine} fontSize="md" />}
          >
            <Text>Listar</Text>
          </Button>

          <Link
            hidden
            download={`booking-payments-reports-${Date.now()}.xlsx`}
            ref={downloadLinkRef}
            href={downloadLinkUrl}
          >
            Download
          </Link>
          <Button
            colorScheme="green"
            size="sm"
            onClick={handleSubmit(handleExportInvoiceReports)}
            leftIcon={<Icon as={RiDownload2Line} fontSize="md" />}
          >
            <Text>Exportar</Text>
          </Button>
        </ButtonGroup>

        <VStack spacing="8">
          <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
            <DatePicker
              label="Data de autorização do pagamento de"
              maxDate={maxDate}
              control={control}
              isClearable={false}
              error={errors.authorizationDateInit}
              {...register('authorizationDateInit')}
            />

            <DatePicker
              label="Data de autorização do pagamento até"
              maxDate={maxDate}
              control={control}
              isClearable={false}
              error={errors.authorizationDateEnd}
              {...register('authorizationDateEnd')}
            />
          </SimpleGrid>

          <Divider />
        </VStack>
      </Flex>

      <AppointmentPaymentsReportTable
        mt="4"
        paymentReports={appointmentPaymentsReportList}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalPages={totalPages}
      />
    </Box>
  );
};
